import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ABOUT } from '../constants/testIds';

const AboutSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };
  
  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.8 }
    }
  };
  
  return (
    <section 
      id="historia"
      data-testid={ABOUT.section}
      className="py-32 bg-gradient-to-b from-[#F2EBE1] to-[#FDFBF7] relative overflow-hidden"
    >
      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 w-64 h-64 bg-[#B58A3F]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-80 h-80 bg-[#2B4C36]/5 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-6 relative">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid lg:grid-cols-2 gap-20 items-center"
        >
          {/* Text Content */}
          <motion.div variants={itemVariants} className="space-y-8">
            <div>
              <motion.p 
                variants={itemVariants}
                className="text-[#B58A3F] uppercase tracking-[0.3em] text-sm mb-4 font-medium"
              >
                Desde 1960
              </motion.p>
              <motion.h2 
                data-testid={ABOUT.title}
                variants={itemVariants}
                className="heading-serif text-5xl md:text-6xl text-[#142A1D] tracking-tight font-light mb-6"
              >
                Nuestro Legado
              </motion.h2>
            </div>
            
            <motion.div 
              data-testid={ABOUT.content}
              variants={containerVariants}
              className="space-y-6 text-[#142A1D] text-lg leading-relaxed"
            >
              <motion.p variants={itemVariants} className="opacity-90">
                Legatum Coffee nace de una tradición cafetera que ha pasado de generación en generación en las montañas del Huila, Colombia. Desde 1960, nuestra familia ha cultivado café con dedicación, respeto por la tierra y un profundo compromiso con la calidad.
              </motion.p>
              <motion.p variants={itemVariants} className="opacity-90">
                Hoy continuamos ese legado seleccionando únicamente cafés de especialidad, cultivados a gran altura y evaluados bajo los estándares de la <span className="text-[#B58A3F] font-medium">Specialty Coffee Association (SCA)</span>. Cada perfil expresa el carácter único de su finca y de quienes la trabajan día a día.
              </motion.p>
              <motion.p variants={itemVariants} className="opacity-90">
                Más que vender café, queremos compartir el resultado de décadas de experiencia, pasión y esfuerzo familiar en cada taza.
              </motion.p>
              <motion.p variants={itemVariants} className="opacity-90">
                El nombre <span className="text-[#B58A3F] font-medium">Legatum</span> proviene del latín y significa "legado". Para nosotros representa aquello que trasciende generaciones: el amor por nuestra tierra, la excelencia en cada cosecha y el deseo de compartir con el mundo un café colombiano excepcional.
              </motion.p>
            </motion.div>
            
            {/* Stats */}
            <motion.div 
              variants={containerVariants}
              className="grid grid-cols-3 gap-6 pt-8"
            >
              {[
                { value: '1960', label: 'Tradición Familiar' },
                { value: '85+ SCA', label: 'Café de Especialidad' },
                { value: '100% Arábica', label: 'Cultivado en Huila' }
              ].map((stat, index) => (
                <motion.div 
                  key={stat.label}
                  variants={itemVariants}
                  whileHover={{ y: -4 }}
                  className="text-center p-4 bg-white rounded-2xl border border-[#E5DEC5] shadow-sm"
                >
                  <p className="text-2xl md:text-3xl font-light text-[#B58A3F] heading-serif mb-1">{stat.value}</p>
                  <p className="text-xs text-[#142A1D] opacity-60 uppercase tracking-wider">{stat.label}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
          
          {/* Image Grid with Animations */}
          <motion.div 
            variants={containerVariants}
            className="grid grid-cols-2 gap-6"
          >
            <motion.div variants={imageVariants} className="space-y-6">
              <motion.div 
                whileHover={{ scale: 1.05, rotate: 2 }}
                transition={{ duration: 0.3 }}
                className="aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_legatum-coffee/artifacts/6f4ut664_image01.png"
                  alt="Granos de café Legatum"
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.05, rotate: -2 }}
                transition={{ duration: 0.3 }}
                className="aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_legatum-coffee/artifacts/siwk0nsd_image03.png"
                  alt="Taza de café Legatum"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </motion.div>
            
            <motion.div 
              variants={imageVariants}
              className="space-y-6 pt-12"
            >
              <motion.div 
                whileHover={{ scale: 1.05, rotate: -2 }}
                transition={{ duration: 0.3 }}
                className="aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_legatum-coffee/artifacts/ohpxwx0p_image02.png"
                  alt="Cerezas de café en la planta"
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.05, rotate: 2 }}
                transition={{ duration: 0.3 }}
                className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_legatum-coffee/artifacts/x083fnp9_image04.png"
                  alt="Montañas del Huila"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
